<script setup lang="ts">
import { computed, nextTick } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Input from '@uiKit/elements/Input.vue'
import CurrencyText from '@uiKit/components/CurrencyText.vue'

// Big value field on the sell steps (Figma "Amount", 34842:194752 /
// 34842:194779): a prefix left (the token mark via the `prefix` slot, or a
// muted AED), the typed value right, then a hint line that turns into the
// error when the value can't be listed. The `hint-append` slot sits after the
// hint (e.g. an ⓘ sheet).

interface Props {
  /** Left prefix text, e.g. "AED" (rendered as the currency mark). Ignored when the `prefix` slot is used. */
  prefix?: string
  /** Accessible name of the input. */
  label: string
  hint?: string
  error?: string | null
  inputmode?: 'numeric' | 'decimal'
  placeholder?: string
  /** id for the hint / error line, referenced by aria-describedby. */
  describedById: string
  /** Strips characters the field doesn't accept (digits only, 2 decimals…). */
  sanitize?: (text: string) => string
}

const props = withDefaults(defineProps<Props>(), { inputmode: 'numeric', placeholder: '0' })

const value = defineModel<string>({ required: true })

// Write the raw text first, then the cleaned one on the next tick, so the
// input re-renders even when the cleaned value equals the previous one.
const fieldValue = computed({
  get: () => value.value,
  set: (text: string) => {
    const clean = props.sanitize ? props.sanitize(text) : text
    value.value = text
    if (clean !== text) nextTick(() => (value.value = clean))
  },
})

const emit = defineEmits<{ (e: 'blur'): void }>()
</script>

<template>
  <Box direction="col" gap="7px" class="sell-field">
    <Box
      is="label"
      align="center"
      gap="10px"
      :class="['sell-field__box', { 'sell-field__box--invalid': error }]"
    >
      <slot name="prefix">
        <Text
          is="span"
          type="body"
          weight="medium"
          color="var(--p-surface-500)"
          class="sell-field__prefix"
          ><CurrencyText :text="prefix"
        /></Text>
      </slot>
      <Input
        v-model="fieldValue"
        class="sell-field__input"
        :inputmode="inputmode"
        :placeholder="placeholder"
        :invalid="Boolean(error)"
        :aria-label="label"
        :aria-invalid="Boolean(error)"
        :aria-describedby="describedById"
        autocomplete="off"
        @blur="emit('blur')"
      />
    </Box>
    <Box align="center" gap="7px" class="sell-field__hint-row">
      <Text
        :id="describedById"
        is="span"
        type="caption"
        :color="error ? 'var(--p-danger-600)' : 'var(--p-surface-500)'"
        :role="error ? 'alert' : undefined"
        class="sell-field__hint"
        ><CurrencyText :text="error || hint"
      /></Text>
      <slot name="hint-append" />
    </Box>
  </Box>
</template>

<style scoped>
.sell-field {
  width: 100%;
}

.sell-field__box {
  min-height: 56px;
  padding: 0 16px;
  border: 1px solid var(--p-surface-300);
  border-radius: 12px;
  background: var(--p-surface-0);
  box-sizing: border-box;
  cursor: text;
  transition: border-color 0.15s ease;
}

.sell-field__box:focus-within {
  border-color: var(--p-primary-500);
}

.sell-field__box--invalid,
.sell-field__box--invalid:focus-within {
  border: 1.5px solid var(--p-danger-600);
  padding: 0 15.5px;
}

.sell-field__prefix {
  flex-shrink: 0;
  line-height: 1.3;
}

/* The box draws the field chrome; the input itself is bare, right-aligned. */
.sell-field .sell-field__input.p-inputtext,
.sell-field .sell-field__input.p-inputtext:focus,
.sell-field .sell-field__input.p-inputtext:focus-visible {
  flex: 1;
  min-width: 0;
  height: 54px;
  padding: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
  text-align: right;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--p-text-color);
}

.sell-field__hint-row {
  width: 100%;
  flex-wrap: wrap;
}

.sell-field__hint {
  line-height: 1.4;
}
</style>
